import React, { useState } from 'react';
import { Box, Checkbox, Chip, Collapse, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BasicTable from '../../../components/BasicTable/BasicTable';
import RowActionsMenu from '../../../components/RowActionsMenu/RowActionsMenu';
import { ITableHeadings, ITableMetadata } from '../../../common/interfaces/common.interface';
import { PayerBalanceDto } from '../../../common/interfaces/invoicing.interface';
import { LastPaymentCell, MonthChips, OwedCell, PayerCell } from './Debts/DebtCells';

/** The unpaid group has a null id, so it needs a key of its own. */
export const payerKey = (payer: PayerBalanceDto) => payer.payerId ?? 'no-payer';

interface PayerBalancesTableProps {
  kindergartenId: string;
  kindergartenName?: string;
  payers: PayerBalanceDto[];
  loader: boolean;
  metadata: ITableMetadata;
  setMetadata: React.Dispatch<React.SetStateAction<ITableMetadata>>;
  buildMailto: (payer: PayerBalanceDto) => string;
  emptyState?: React.ReactNode;
  /** The "select every row on the page" checkbox, owned by the list above. */
  selectAll: React.ReactNode;
  chosen: Set<string>;
  onToggle: (key: string) => void;
}

/**
 * Debts by family rather than by child.
 *
 * A parent paying for two children pays one sum, so chasing them per child asks the
 * same person twice for halves of a number neither row shows. The row opens the family's
 * children, because the money is owed per invoice and those hang off a child.
 */
const PayerBalancesTable: React.FC<PayerBalancesTableProps> = ({
  kindergartenId,
  kindergartenName,
  payers,
  loader,
  metadata,
  setMetadata,
  buildMailto,
  emptyState,
  selectAll,
  chosen,
  onToggle
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string[]>([]);
  const toggle = (key: string) => setExpanded(prev => (prev.includes(key) ? prev.filter(id => id !== key) : [...prev, key]));

  const headings: ITableHeadings[] = [
    { id: 0, value: '', label: '', sortable: false, width: 48, content: selectAll },
    { id: 1, value: '', label: 'contracts.payer', sortable: false, width: '20%' },
    { id: 2, value: '', label: 'kindergarten.tabs.children', sortable: false, width: '18%' },
    { id: 3, value: '', label: 'balances.columns.unpaid', sortable: false },
    { id: 4, value: '', label: 'balances.columns.owes', sortable: false, align: 'right', width: 150 },
    { id: 5, value: '', label: 'balances.columns.lastPayment', sortable: false, width: 130 },
    { id: 6, value: '', label: '', sortable: false, width: 88 }
  ];

  return (
    <BasicTable headings={headings} loader={loader} metadata={metadata} setMetadata={setMetadata} emptyState={emptyState}>
      {payers.map(payer => {
        const key = payerKey(payer);
        const isOpen = expanded.includes(key);
        return (
          <React.Fragment key={key}>
            <TableRow
              hover
              tabIndex={0}
              aria-expanded={isOpen}
              selected={chosen.has(key)}
              onClick={() => toggle(key)}
              onKeyDown={event => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggle(key);
                }
              }}
              sx={{ cursor: 'pointer', verticalAlign: 'top' }}
            >
              <TableCell padding="checkbox" onClick={event => event.stopPropagation()}>
                {/* children without a payer have nobody to export a row for */}
                {payer.payerId && (
                  <Checkbox
                    size="small"
                    checked={chosen.has(key)}
                    onChange={() => onToggle(key)}
                    slotProps={{ input: { 'aria-label': t('balances.select', { name: payer.payerFullName }) } }}
                  />
                )}
              </TableCell>
              <TableCell>
                {payer.payerFullName ? (
                  <PayerCell name={payer.payerFullName} email={payer.payerEmail} />
                ) : (
                  <Chip size="small" color="warning" variant="outlined" label={t('contracts.noPayerSet')} />
                )}
              </TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 14 }}>{payer.children.map(child => child.childFullName.split(' ')[0]).join(', ')}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('balances.cards.children', { count: payer.childCount })}</Typography>
              </TableCell>
              <TableCell>
                <MonthChips invoices={payer.openInvoices} credit={payer.credit} withChild={payer.children.length > 1} />
              </TableCell>
              <TableCell>
                <OwedCell
                  outstanding={payer.outstanding}
                  overdueAmount={payer.overdueAmount}
                  daysOverdue={payer.daysOverdue}
                  credit={payer.credit}
                  invoices={payer.openInvoices}
                />
              </TableCell>
              <TableCell>
                <LastPaymentCell payment={payer.lastPayment} />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center">
                  <RowActionsMenu
                    name={payer.payerFullName ?? t('contracts.noPayerSet')}
                    actions={[
                      {
                        label: t('balances.emailFamily', { name: payer.payerFullName, count: payer.childCount }),
                        hidden: !payer.payerEmail || payer.outstanding <= 0,
                        onClick: () => window.open(buildMailto(payer), '_blank', 'noopener')
                      }
                    ]}
                  />
                  <IconButton
                    size="small"
                    aria-label={isOpen ? t('balances.hideChildren') : t('balances.showChildren')}
                    onClick={event => {
                      event.stopPropagation();
                      toggle(key);
                    }}
                  >
                    {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={headings.length} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ py: 1, pl: 7 }}>
                    {payer.children.map(child => (
                      <Stack
                        key={child.childId}
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{ py: 0.75, cursor: 'pointer', maxWidth: 640 }}
                        onClick={() => navigate(`/${kindergartenId}/child/${child.childId}/details/${encodeURIComponent(kindergartenName ?? '')}`)}
                      >
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{child.childFullName}</Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {[child.groupName, child.locationName].filter(Boolean).join(' · ') || '—'}
                          </Typography>
                        </Box>
                        <OwedCell
                          outstanding={child.outstanding}
                          overdueAmount={child.overdueAmount}
                          daysOverdue={child.daysOverdue}
                          credit={child.credit}
                          invoices={payer.openInvoices.filter(invoice => invoice.childFirstName === child.childFullName.split(' ')[0])}
                        />
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          </React.Fragment>
        );
      })}
    </BasicTable>
  );
};

export default PayerBalancesTable;
